import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useCreateProject } from '@/hooks/useProjects';
import { usePipelineStatuses } from '@/hooks/usePipelines';
import type { Pipeline } from '@/hooks/usePipelines';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipeline: Pipeline;
  defaultStatusId?: string | null;
  defaultJobId?: string | null;
}

export function CreateProjectDialog({
  open,
  onOpenChange,
  pipeline,
  defaultStatusId,
  defaultJobId,
}: CreateProjectDialogProps) {
  const { data: statuses } = usePipelineStatuses(pipeline.id);
  const createProject = useCreateProject();

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [statusId, setStatusId] = useState<string>(defaultStatusId ?? '');

  // Reset form whenever the dialog opens or defaultStatusId changes (e.g. different column)
  useEffect(() => {
    if (open) {
      setName('');
      setNotes('');
      setStatusId(defaultStatusId ?? '');
    }
  }, [open, defaultStatusId]);

  const handleSubmit = () => {
    if (!name.trim()) return;
    createProject.mutate(
      {
        pipelineId: pipeline.id,
        statusId: statusId || null,
        name: name.trim(),
        notes: notes.trim() || undefined,
        jobId: defaultJobId ?? undefined,
      },
      {
        onSuccess: () => {
          setName('');
          setNotes('');
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="proj-name">Project Name</Label>
            <Input
              id="proj-name"
              placeholder="e.g. Smith Residence Installation"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="proj-status">Stage</Label>
            <Select value={statusId} onValueChange={setStatusId}>
              <SelectTrigger id="proj-status">
                <SelectValue placeholder="Select stage..." />
              </SelectTrigger>
              <SelectContent>
                {statuses?.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="proj-notes">Notes (optional)</Label>
            <Textarea
              id="proj-notes"
              placeholder="Any notes about this project..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!name.trim() || createProject.isPending}>
              {createProject.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Project
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
